import { useCallback, useEffect, useState } from 'react'
import { MinimalAlert, MinimalCheckbox, MinimalDropdown, MinimalInput } from '@ovasabi/ui-minimal'
import { useSearchParams } from 'react-router-dom'
import { commandPayload, commands, fetchVerificationDocument } from '../../lib/runtime'
import { readVerificationPaused } from '../choosechow/mealCategories'
import * as S from './OpsStyle'
import { formatWhen } from './opsData'

/**
 * Verification review console (Chef Verification & Hygiene Standards).
 *
 * The queue holds onboarding and re-verification submissions, certificates
 * awaiting review, and open hygiene flags. A decision always records an
 * inspection (method, outcome, notes): those records are the platform's
 * due-diligence evidence, so the console never offers a one-click verify.
 *
 * Documents are private. They are fetched with the operator's token and shown
 * from memory; nothing is cached by the browser (the server sends no-store).
 */

type Row = Record<string, unknown>
const text = (v: unknown) => String(v ?? '')

const DocPreview = ({ label, docKey }: { label: string; docKey: string }) => {
  const [url, setUrl] = useState('')
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    if (!docKey) return undefined
    let revoked = ''
    let alive = true
    fetchVerificationDocument(docKey)
      .then((objectUrl) => {
        revoked = objectUrl
        if (alive) setUrl(objectUrl)
      })
      .catch(() => alive && setFailed(true))
    return () => {
      alive = false
      if (revoked) URL.revokeObjectURL(revoked)
    }
  }, [docKey])

  if (!docKey) return <S.Note>{label}: not uploaded</S.Note>
  if (failed) return <S.ErrorNote>{label}: could not load</S.ErrorNote>
  if (!url) return <S.Note>{label}: loading…</S.Note>
  const lower = docKey.toLowerCase()
  return (
    <figure style={{ margin: 0, display: 'grid', gap: 4 }}>
      <figcaption style={{ fontSize: 12, fontWeight: 600 }}>{label}</figcaption>
      {/\.(mp4|mov|webm)$/.test(lower) ? (
        <video src={url} controls style={{ maxWidth: '100%', maxHeight: 260 }} />
      ) : lower.endsWith('.pdf') ? (
        <a href={url} target="_blank" rel="noreferrer">Open PDF</a>
      ) : (
        <img src={url} alt={label} style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }} />
      )}
    </figure>
  )
}

const Dossier = ({ vendorId, onChanged }: { vendorId: string; onChanged: () => void }) => {
  const [view, setView] = useState<Row | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [method, setMethod] = useState('remote')
  const [notes, setNotes] = useState('')
  const [selfieMatches, setSelfieMatches] = useState(false)
  const [phoneByCall, setPhoneByCall] = useState(false)
  const [approveCert, setApproveCert] = useState(false)
  const [outcome, setOutcome] = useState('pass')
  const [badgeCap, setBadgeCap] = useState('')
  const [payout, setPayout] = useState<Row | null>(null)
  const [payoutError, setPayoutError] = useState('')

  /* Without this a chef's bank account stayed 'pending' forever: nothing
     registered it with Paystack, so every order fell back to a platform hold
     and no split ever reached a chef. The server refuses unless the kitchen is
     verified and the bank-confirmed name matches, and a repeat is a no-op. */
  const provisionPayout = async () => {
    setBusy(true)
    setPayoutError('')
    try {
      setPayout(commandPayload(await commands.verification.provisionPayoutAccount(vendorId)))
    } catch (err) {
      setPayoutError(err instanceof Error ? err.message : 'Could not register the payout account.')
    } finally {
      setBusy(false)
    }
  }

  const act = useCallback(async (action: () => Promise<unknown>) => {
    setBusy(true)
    setError('')
    try {
      const next = commandPayload(await action())
      if (next.vendor) setView(next)
      if (next.payout_account && typeof next.payout_account === 'object') {
        setPayout(next.payout_account as Row)
      }
      setNotes('')
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed.')
    } finally {
      setBusy(false)
    }
  }, [onChanged])

  useEffect(() => {
    setView(null)
    void act(() => commands.verification.getDossier(vendorId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId])

  if (!view) return <S.Card>{error ? <S.ErrorNote>{error}</S.ErrorNote> : <S.Note>Loading dossier…</S.Note>}</S.Card>
  const vendor = (view.vendor ?? {}) as Row
  const d = (view.dossier ?? {}) as Row
  const photos = (d.kitchen_photos ?? {}) as Record<string, string>
  const flags = (view.flags ?? []) as Row[]
  const inspections = (view.inspections ?? []) as Row[]
  const missing = (view.missing ?? []) as string[]
  const status = text(vendor.verification_status)
  const openFlags = flags.filter((f) => ['open', 'reviewing'].includes(text(f.status)))

  return (
    <S.Card>
      <S.CardTitle>
        {text(vendor.display_name)} · <S.Pill $tone={status === 'verified' ? 'ok' : status === 'suspended' ? 'bad' : 'warn'}>{status}</S.Pill>{' '}
        <S.Pill $tone="flat">{text(vendor.badge_tier)}</S.Pill>
      </S.CardTitle>
      {error && <S.ErrorNote>{error}</S.ErrorNote>}
      <S.Note>
        Submission: {text(d.submission_kind)} · submitted {formatWhen(text(d.submitted_at)) || '—'} · reviewed{' '}
        {formatWhen(text(d.reviewed_at)) || '—'} · re-verify by {formatWhen(text(vendor.reverification_due)) || '—'}
      </S.Note>
      {missing.length > 0 && <S.Note>Still missing: {missing.join(', ')}</S.Note>}
      {d.remediation_required === true && <S.ErrorNote>Remediation required before reinstatement.</S.ErrorNote>}

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
        <DocPreview label={`ID (${text(d.id_document_type) || '—'})`} docKey={text(d.id_document_key)} />
        <DocPreview label="Selfie" docKey={text(d.selfie_key)} />
        {Object.entries(photos).map(([slot, key]) => (
          <DocPreview key={slot} label={slot.replace(/_/g, ' ')} docKey={key} />
        ))}
        <DocPreview label="Kitchen video" docKey={text(d.kitchen_video_key)} />
        <DocPreview label={`Certificate (${text(d.cert_issuer)}, expires ${text(d.cert_expiry) || '—'})`} docKey={text(d.food_handler_cert_key)} />
      </div>
      <S.Note>
        Phone: {text(d.phone_number) || 'not confirmed'} {d.phone_verified_at ? '✓' : ''} · Agreement:{' '}
        {text(d.agreement_version) || 'not accepted'}
      </S.Note>

      <S.FormGrid>
        <MinimalDropdown label="Inspection method" value={method} onChange={setMethod}
          options={[{ value: 'remote', label: 'Remote (photos + video)' }, { value: 'in_person', label: 'In person' }]} />
        <MinimalInput label="Notes" hint="Shown to the chef on rejection." value={notes} onChange={(event) => setNotes(event.target.value)} />
      </S.FormGrid>

      {view.review_pending === true && (
        <>
          <MinimalCheckbox checked={selfieMatches} onCheckedChange={setSelfieMatches} label="Selfie matches the ID photo" />
          {!d.phone_verified_at && (
            <MinimalCheckbox checked={phoneByCall} onCheckedChange={setPhoneByCall} label="I confirmed the phone number by calling it" />
          )}
          {d.food_handler_cert_key && !d.cert_approved_at ? (
            <MinimalCheckbox checked={approveCert} onCheckedChange={setApproveCert} label="Certificate is genuine and in date" />
          ) : null}
          <div>
            <S.Action disabled={busy} onClick={() => void act(() => commands.verification.reviewSubmission(vendorId, {
              decision: 'approve', method, notes, selfie_matches_id: selfieMatches,
              phone_confirmed_by_call: phoneByCall, approve_certificate: approveCert,
            }))}>Approve</S.Action>{' '}
            <S.Danger disabled={busy || !notes} onClick={() => void act(() => commands.verification.reviewSubmission(vendorId, { decision: 'reject', method, notes }))}>
              Reject
            </S.Danger>
          </div>
        </>
      )}

      {view.review_pending !== true && d.food_handler_cert_key && !d.cert_approved_at ? (
        <div>
          <S.Action disabled={busy} onClick={() => void act(() => commands.verification.reviewCertificate(vendorId, 'approve', notes))}>Approve certificate</S.Action>{' '}
          <S.Danger disabled={busy} onClick={() => void act(() => commands.verification.reviewCertificate(vendorId, 'reject', notes))}>Reject certificate</S.Danger>
        </div>
      ) : null}

      <S.CardTitle>Payouts</S.CardTitle>
      <div>
        <S.Note>
          Registers the chef&apos;s bank account with Paystack so each order&apos;s food share settles to the chef directly.
          Until then their share is held by ChooseChow and shows under receivables.
        </S.Note>
        {payout && (
          <S.Note>
            {text(payout.bank_name)} ····{text(payout.account_tail)} ·{' '}
            <S.Pill $tone={payout.payout_ready === true ? 'ok' : 'warn'}>{payout.payout_ready === true ? 'settling to chef' : text(payout.status)}</S.Pill>
          </S.Note>
        )}
        {payoutError && <S.ErrorNote>{payoutError}</S.ErrorNote>}
        <S.Action disabled={busy || status !== 'verified'} onClick={() => void provisionPayout()}>
          {payout?.payout_ready === true ? 'Payout account registered' : 'Register payout account with Paystack'}
        </S.Action>
        {status !== 'verified' && <S.Note>Approve the kitchen first.</S.Note>}
      </div>

      <S.CardTitle>Spot check</S.CardTitle>
      <div>
        <MinimalDropdown label="Inspection outcome" value={outcome} onChange={setOutcome} options={[{ value: 'pass', label: 'Pass' }, { value: 'conditional', label: 'Conditional' }, { value: 'fail', label: 'Fail (suspends a selling kitchen)' }]} />

        <S.Action disabled={busy} onClick={() => void act(() => commands.verification.recordInspection(vendorId, { type: 'spot_check', method, outcome, notes }))}>
          Record inspection
        </S.Action>
        {status === 'suspended' && openFlags.length === 0 && (
          <>
            {' '}
            <S.Action disabled={busy} onClick={() => void act(() => commands.verification.reinstate(vendorId, { method, notes }))}>
              Reinstate after passed inspection
            </S.Action>
          </>
        )}
      </div>

      <S.CardTitle>Kitchen Operations</S.CardTitle>
      <div>
        {status !== 'suspended' && status !== 'revoked' && (
          <S.Danger disabled={busy || !notes} onClick={() => void act(() => commands.verification.suspendKitchen(vendorId, notes))}>
            Suspend kitchen
          </S.Danger>
        )}
        {status !== 'revoked' && (
          <>
            {' '}
            <S.Danger
              disabled={busy || !notes}
              onClick={() => {
                if (window.confirm(`Are you sure you want to delete ${text(vendor.display_name)}? This will archive all menus and dishes, and decommission the kitchen.`)) {
                  void act(() => commands.verification.deleteKitchen(vendorId, notes))
                }
              }}
            >
              Delete kitchen
            </S.Danger>
          </>
        )}
        {status !== 'revoked' && <S.Note>Enter notes in the inspection form above as the reason to suspend or delete this kitchen.</S.Note>}
      </div>

      <S.CardTitle>Hygiene flags ({flags.length})</S.CardTitle>
      {flags.length === 0 ? <S.Empty>No hygiene reports.</S.Empty> : (
        <S.TableScroll>
          <S.Table>
            <thead><tr><th>Reported</th><th>Reason</th><th>Detail</th><th>Status</th><th>Decision</th></tr></thead>
            <tbody>
              {flags.map((f) => (
                <tr key={text(f.id)}>
                  <td>{formatWhen(text(f.created_at))}</td>
                  <td>{text(f.reason)}</td>
                  <td>{text(f.detail) || '—'}</td>
                  <td>{text(f.status)}{f.resolution_notes ? ` — ${text(f.resolution_notes)}` : ''}</td>
                  <td>
                    {['open', 'reviewing'].includes(text(f.status)) ? (
                      <>
                        <MinimalDropdown label="Badge cap if upheld" value={badgeCap} onChange={setBadgeCap} options={[{ value: '', label: 'No badge cap' }, { value: 'verified', label: 'Cap at Verified' }, { value: 'hygiene_certified', label: 'Cap at Hygiene Certified' }]} />

                        <S.Danger disabled={busy || !notes} onClick={() => void act(() => commands.verification.resolveFlag(text(f.id), { decision: 'uphold', notes, badge_cap: badgeCap }))}>Uphold</S.Danger>{' '}
                        <S.Action disabled={busy || !notes} onClick={() => void act(() => commands.verification.resolveFlag(text(f.id), { decision: 'dismiss', notes }))}>Dismiss</S.Action>
                      </>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </S.Table>
        </S.TableScroll>
      )}
      {openFlags.length > 0 && <S.Note>Resolving a flag needs notes. Upholding keeps the kitchen suspended until reinstated.</S.Note>}

      <S.CardTitle>Inspection history ({inspections.length})</S.CardTitle>
      {inspections.length === 0 ? <S.Empty>No inspections recorded.</S.Empty> : (
        <S.TableScroll>
          <S.Table>
            <thead><tr><th>When</th><th>Type</th><th>Method</th><th>Outcome</th><th>Notes</th></tr></thead>
            <tbody>
              {inspections.map((i) => (
                <tr key={text(i.id)}>
                  <td>{formatWhen(text(i.performed_at))}</td><td>{text(i.type)}</td><td>{text(i.method)}</td>
                  <td>{text(i.outcome)}</td><td>{text(i.notes) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </S.Table>
        </S.TableScroll>
      )}
    </S.Card>
  )
}

export const OpsVerificationPage = () => {
  const [params, setParams] = useSearchParams()
  const selected = params.get('kitchen') ?? ''
  const [paused, setPaused] = useState(true)
  const [queue, setQueue] = useState<{ submissions: Row[]; flags: Row[] }>({ submissions: [], flags: [] })
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      const [review, verificationPaused] = await Promise.all([commands.verification.listReviewQueue(), readVerificationPaused(true)])
      const p = commandPayload(review)
      setPaused(verificationPaused)
      setQueue({ submissions: (p.submissions ?? []) as Row[], flags: (p.flags ?? []) as Row[] })
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the review queue.')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const open = (vendorId: string) => setParams({ kitchen: vendorId })

  return (
    <>
      <div>
        <S.H1>Verification</S.H1>
        {paused && <MinimalAlert tone="info" title="Verification intake paused">Chefs should complete kitchen addresses in Profile. Existing records, hygiene reports, and suspensions remain available for operations review.</MinimalAlert>}
      </div>
      {error && <S.ErrorNote>{error}</S.ErrorNote>}

      <S.Card>
        <S.CardTitle>Open hygiene flags ({queue.flags.length})</S.CardTitle>
        {queue.flags.length === 0 ? <S.Empty>No open hygiene reports.</S.Empty> : (
          <S.TableScroll>
            <S.Table>
              <thead><tr><th>Kitchen</th><th>Reason</th><th>Reported</th><th>Paused</th><th /></tr></thead>
              <tbody>
                {queue.flags.map((f) => (
                  <tr key={text(f.id)}>
                    <td>{text(f.kitchen_name)}</td><td>{text(f.reason)}</td><td>{formatWhen(text(f.created_at))}</td>
                    <td>{f.auto_paused ? 'yes' : 'no'}</td>
                    <td><S.Action onClick={() => open(text(f.vendor_profile_id))}>Review</S.Action></td>
                  </tr>
                ))}
              </tbody>
            </S.Table>
          </S.TableScroll>
        )}
      </S.Card>

      <S.Card>
        <S.CardTitle>Awaiting review ({queue.submissions.length})</S.CardTitle>
        {queue.submissions.length === 0 ? <S.Empty>Nothing waiting.</S.Empty> : (
          <S.TableScroll>
            <S.Table>
              <thead><tr><th>Kitchen</th><th>Kind</th><th>Status</th><th>Since</th><th /></tr></thead>
              <tbody>
                {queue.submissions.map((row) => (
                  <tr key={text(row.vendor_profile_id)}>
                    <td>{text(row.display_name)}</td><td>{text(row.submission_kind).replace(/_/g, ' ')}</td>
                    <td>{text(row.verification_status)}</td><td>{formatWhen(text(row.submitted_at))}</td>
                    <td><S.Action onClick={() => open(text(row.vendor_profile_id))}>Open dossier</S.Action></td>
                  </tr>
                ))}
              </tbody>
            </S.Table>
          </S.TableScroll>
        )}
      </S.Card>

      {selected && <Dossier vendorId={selected} onChanged={() => void load()} />}
    </>
  )
}
